package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.merkost.handpass.sharedweb.theme.Accent
import com.merkost.handpass.sharedweb.theme.AccentSoft
import com.merkost.handpass.sharedweb.theme.AccentText
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.HandpassTypography
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusButton
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.merkost.handpass.sharedweb.theme.Transparent
import com.merkost.handpass.sharedweb.theme.WebMotion
import com.merkost.handpass.sharedweb.theme.WebSize
import com.merkost.handpass.sharedweb.theme.transitionSurfaceAction
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedBy
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLButtonElement

data class WebTab<T>(val key: T, val label: String, val countBadge: Long? = null)

val WebTabPillStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .cursor(Cursor.Pointer)
            .borderRadius(RadiusButton.value())
            .color(OnSurfaceMuted.value())
            .fontWeight(500)
            .minHeight(TouchTarget.value())
            .transitionSurfaceAction()
            .styleModifier {
                property("font-size", "${HandpassTypography.Caption}")
                property("padding", "${HandpassSpacing.XS} ${HandpassSpacing.MD}")
                property("white-space", "nowrap")
                property("gap", "${HandpassSpacing.XS}")
                property("border", "none")
                property("font-family", "inherit")
                property("background", Transparent.value().toString())
            }
    }
    hover {
        Modifier
            .backgroundColor(SurfaceMuted.value())
            .color(OnSurface.value())
    }
    active {
        Modifier.scale(WebMotion.PressScale)
    }
    focusVisible {
        Modifier
            .outline(WebSize.P2.value(), LineStyle.Solid, OnSurface.value())
            .styleModifier { property("outline-offset", WebSize.P2.value().toString()) }
    }
}

val WebTabPillActiveStyle = WebTabPillStyle.extendedBy {
    base {
        Modifier
            .backgroundColor(AccentSoft.value())
            .color(AccentText.value())
            .fontWeight(600)
    }
    hover {
        Modifier
            .backgroundColor(AccentSoft.value())
            .color(AccentText.value())
    }
}

val WebTabsRowStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .gap(HandpassSpacing.XS)
        .styleModifier {
            property("flex-wrap", "wrap")
            property("margin-bottom", "${HandpassSpacing.XL}")
        }
}

@Composable
fun <T> WebTabs(
    tabs: List<WebTab<T>>,
    selected: T,
    onSelect: (T) -> Unit,
    label: String? = null,
    idPrefix: String? = null,
) {
    if (tabs.isEmpty()) return
    val tabElements = remember(tabs.size) { MutableList<HTMLElement?>(tabs.size) { null } }
    Div(attrs = WebTabsRowStyle.toAttrs {
        attr("role", "tablist")
        label?.let { attr("aria-label", it) }
    }) {
        tabs.forEachIndexed { index, tab ->
            val isActive = tab.key == selected
            val pillStyle = if (isActive) WebTabPillActiveStyle else WebTabPillStyle
            Button(
                attrs = pillStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                    attr("role", "tab")
                    attr("type", "button")
                    attr("aria-selected", isActive.toString())
                    attr("tabindex", if (isActive) "0" else "-1")
                    idPrefix?.let {
                        attr("id", "$it-tab-$index")
                        if (isActive) attr("aria-controls", "$it-panel-$index")
                    }
                    onClick { onSelect(tab.key) }
                    ref { element ->
                        tabElements[index] = element
                        onDispose {
                            if (tabElements[index] === element) tabElements[index] = null
                        }
                    }
                    onKeyDown { event ->
                        when (event.key) {
                            "ArrowRight" -> {
                                event.preventDefault()
                                val nextIndex = (index + 1) % tabs.size
                                tabElements[nextIndex]?.focus()
                                onSelect(tabs[nextIndex].key)
                            }
                            "ArrowLeft" -> {
                                event.preventDefault()
                                val nextIndex = (index - 1 + tabs.size) % tabs.size
                                tabElements[nextIndex]?.focus()
                                onSelect(tabs[nextIndex].key)
                            }
                            "Home" -> {
                                event.preventDefault()
                                tabElements.firstOrNull()?.focus()
                                onSelect(tabs.first().key)
                            }
                            "End" -> {
                                event.preventDefault()
                                tabElements.lastOrNull()?.focus()
                                onSelect(tabs.last().key)
                            }
                        }
                    }
                }
            ) {
                Text(tab.label)
                if (tab.countBadge != null) {
                    Span(
                        attrs = Modifier
                            .color(if (isActive) AccentText.value() else OnSurfaceMuted.value())
                            .styleModifier {
                                property("font-family", HandpassTypography.MonoFamily)
                                property("font-size", "${HandpassTypography.SM}")
                                property("font-variant-numeric", "tabular-nums")
                            }
                            .toAttrs()
                    ) {
                        Text("(${tab.countBadge})")
                    }
                }
            }
        }
    }
}
